import { useEffect, useState } from 'react';

import { useMediaQuery } from '@react-hookz/web';
import { Form, Formik } from 'formik';
import { useInView } from 'react-intersection-observer';
import { useHistory, useLocation } from 'react-router';
import * as Yup from 'yup';

import { Typography } from 'src/app/components/Elements/Typography';
import { useOnboardingZipCodePersistentData } from 'src/app/hooks/feature_data/useOnboardingZipCodePersistentData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { getStarted } from 'src/app/router/paths';

import { OnboardingZipCode } from '../../GetStarted/OnboardingZipCode';
import { ELEMENT_IDS, ZipcodeCheckerStatus } from '../constants';

import {
  ButtonContainer,
  ButtonText,
  Container,
  Row,
  StyledButton,
  TextWrapper,
  WaitListSpan,
  ZipWrapper,
} from './styles';
import { ZipcodeCheckerItem } from './ZipcodeCheckerItem';

export const ZipcodeChecker = ({ handleWaitListClick }: { handleWaitListClick: (zipCode: string) => void }) => {
  const [status, setStatus] = useState(ZipcodeCheckerStatus.DEFAULT);
  const trackRemoveFromCartEvent = useSegmentEvent('viewDeliveryAreaEligibilityForm', { shouldTrackOnMount: false });
  const smDown = useMediaQuery('(max-width: 575px)');
  const mdDown = useMediaQuery('(max-width: 890px)');
  const location = useLocation();
  const { pathname } = location;

  const { inView, ref } = useInView({ threshold: 0.25 });

  useEffect(() => {
    if (inView) {
      trackRemoveFromCartEvent({ pathname });
    }
  }, [inView, trackRemoveFromCartEvent, pathname]);

  const { push } = useHistory();
  const { onboardingZipCodePersistentData } = useOnboardingZipCodePersistentData();
  const [zipCode, setZipCode] = useState('');

  useEffect(() => {
    if (onboardingZipCodePersistentData) {
      setZipCode(onboardingZipCodePersistentData.zipCode);
    }
  }, [onboardingZipCodePersistentData]);

  const handleSubmit = () => {
    if (onboardingZipCodePersistentData) {
      const { zipCode, isDeliverable } = onboardingZipCodePersistentData;

      if (zipCode === '') {
        return;
      }

      if (isDeliverable) {
        push(`${getStarted.route}?isDeliverable=${isDeliverable}&step=eligibility_confirmation&zipCode=${zipCode}`);
      } else {
        setStatus(ZipcodeCheckerStatus.INVALID);
        setZipCode(zipCode);
      }
    }
  };
  const resetZipcodeField = () => {
    setStatus(ZipcodeCheckerStatus.DEFAULT);
  };

  return (
    <div ref={ref}>
      <Container fluid>
        {status === ZipcodeCheckerStatus.DEFAULT && (
          <Formik
            initialValues={{ [ELEMENT_IDS.zipcodeInputField]: zipCode }}
            validationSchema={Yup.object().shape({
              [ELEMENT_IDS.zipcodeInputField]: Yup.string().required('').min(5, '').label('Zip code'),
            })}
            onSubmit={handleSubmit}
          >
            <Form>
              <Row>
                <ZipcodeCheckerItem colSize={smDown ? '12' : 'auto'} borderRight>
                  {/* Adds a line break on screen-sizes between mobile and 890px */}
                  <TextWrapper>
                    <Typography variant="h3">Check if we {mdDown && !smDown && <br />} deliver to you</Typography>
                  </TextWrapper>
                </ZipcodeCheckerItem>

                <ZipcodeCheckerItem colSize={smDown ? '6' : ''}>
                  <ZipWrapper>
                    <OnboardingZipCode
                      name={ELEMENT_IDS.zipcodeInputField}
                      label="Zip Code"
                      successMessage="You're in our delivery area."
                      defaultValue={zipCode}
                    />
                  </ZipWrapper>
                </ZipcodeCheckerItem>

                <ZipcodeCheckerItem colSize={smDown ? '6' : 'auto'}>
                  <ButtonContainer>
                    <StyledButton type="submit" size="medium" style="secondary">
                      <ButtonText>Continue</ButtonText>
                    </StyledButton>
                  </ButtonContainer>
                </ZipcodeCheckerItem>
              </Row>
            </Form>
          </Formik>
        )}

        {status === ZipcodeCheckerStatus.INVALID && (
          <Row>
            <ZipcodeCheckerItem>
              <TextWrapper>
                <Typography variant="h3">We're not in {zipCode} yet</Typography>
              </TextWrapper>
              <TextWrapper>
                <Typography variant="body" marginBottom={'none'}>
                  Try your work address or add your email to our{' '}
                  <WaitListSpan onClick={() => handleWaitListClick(zipCode)}>waitlist</WaitListSpan> and we’ll let you
                  know when we’re in your area.
                </Typography>
              </TextWrapper>
            </ZipcodeCheckerItem>

            <ZipcodeCheckerItem colSize={smDown ? '12' : 'auto'} borderLeft>
              <ButtonContainer>
                <StyledButton type="button" size="medium" style="secondary" onClick={resetZipcodeField} fixedWidth>
                  <ButtonText>Retry</ButtonText>
                </StyledButton>
              </ButtonContainer>
            </ZipcodeCheckerItem>
          </Row>
        )}
      </Container>
    </div>
  );
};
